import { FC, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { debounce } from 'lodash';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  debounceMs?: number;
  fullWidth?: boolean;
}

export const SearchInput: FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  className = '',
  debounceMs = 300,
  fullWidth = false,
}) => {
  const [localValue, setLocalValue] = useState(value);

  const debouncedOnChangeRef = useRef(
    debounce((newValue: string) => {
      onChange(newValue);
    }, debounceMs)
  );

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      debouncedOnChangeRef.current.cancel();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedOnChangeRef.current(newValue);
  };

  return (
    <div
      className={`flex w-full ${fullWidth ? '' : 'lg:w-97.25'} h-11 px-4 justify-between items-center rounded-lg bg-(--neutral-white-NW1000) border-[1.5px] border-(--neutral-white-NW800) ${className}`}
    >
      <input
        type="text"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        className="w-full h-full [font-size:var(--fs-p4)]! focus:outline-none border-0 bg-transparent"
      />
      <Search size={20} className="text-(--neutral-black-NB600) cursor-pointer shrink-0" />
    </div>
  );
};
